import { useInfiniteQuery, useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { taskApi, type TaskStatus, type CreateTaskPayload } from "../lib/taskApi";

export const TASK_KEYS = {
  all: ["tasks"] as const,
  mine: (status?: TaskStatus) => [...TASK_KEYS.all, "mine", status ?? "ALL"] as const,
  mineDetail: (id: string) => [...TASK_KEYS.all, "mine", "detail", id] as const,
  adminList: (status: TaskStatus) => [...TASK_KEYS.all, "admin", status] as const,
};

const QUERY_OPTIONS = {
  staleTime: 0,
  refetchOnMount: true,
  refetchOnWindowFocus: true,
} as const;

export function useMyTasks(status?: TaskStatus) {
  return useInfiniteQuery({
    queryKey: TASK_KEYS.mine(status),
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      taskApi.listMine(status, { limit: 10, cursor: pageParam }),
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    initialPageParam: undefined as string | undefined,
    ...QUERY_OPTIONS,
  });
}

export function useMyTask(taskId: string) {
  return useQuery({
    queryKey: TASK_KEYS.mineDetail(taskId),
    queryFn: () => taskApi.getMine(taskId).then((r) => r.task),
    enabled: !!taskId,
    ...QUERY_OPTIONS,
  });
}

export function useAdminTasks(status: TaskStatus, enabled = true) {
  return useQuery({
    queryKey: TASK_KEYS.adminList(status),
    queryFn: () => taskApi.adminList(status),
    enabled,
    ...QUERY_OPTIONS,
  });
}

export function useCreateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateTaskPayload) => taskApi.adminCreate(payload),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: TASK_KEYS.all });
    },
  });
}

export function useUpdateTaskStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, status }: { taskId: string; status: TaskStatus }) =>
      taskApi.updateStatus(taskId, status),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: TASK_KEYS.all });
    },
  });
}

export function useAddTaskUpdate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, note }: { taskId: string; note: string }) =>
      taskApi.addUpdate(taskId, note),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: TASK_KEYS.all });
    },
  });
}
